import React, { useEffect, useState } from "react";
import { IonPage, IonContent, IonHeader, IonToolbar, IonTitle } from "@ionic/react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStudent } from "../contexts/StudentContext";
import { getCatalog } from "../services/contentService";
import type { Catalog, Year } from "../content/types";
import { YEARS } from "../content/types";
import { chaptersForYear, subjectCompletion } from "../utils/progressStats";
import ProgressBar from "../components/ProgressBar";

export const YearChips: React.FC<{ value: Year; onChange: (y: Year) => void; own: Year }> = ({ value, onChange, own }) => {
  const { t } = useTranslation();
  return (
    <div className="flex gap-2">
      {YEARS.map((y) => (
        <button
          key={y}
          type="button"
          onClick={() => onChange(y)}
          className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${
            value === y ? "bg-primary-500 border-primary-400 text-white" : "bg-white/5 border-white/15 text-gray-300"
          }`}>
          {t("common.yearShort", { year: y })}
          {y === own && value !== y ? " •" : ""}
        </button>
      ))}
    </div>
  );
};

const Subjects: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { profile, progress } = useStudent();
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [year, setYear] = useState<Year>(profile?.year || 9);

  useEffect(() => {
    getCatalog().then(setCatalog).catch(() => undefined);
  }, []);
  useEffect(() => {
    if (profile?.year) setYear(profile.year);
  }, [profile?.year]);

  return (
    <IonPage>
      <IonHeader className="ion-no-border">
        <IonToolbar>
          <IonTitle>{t("subjects.title")}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="page-container wide px-4 py-3 space-y-4 animate-fade-in">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-300">{t("subjects.yearLabel")}</p>
            {profile && <YearChips value={year} onChange={setYear} own={profile.year} />}
          </div>
          {profile && year !== profile.year && <p className="text-xs text-gray-400">{t("subjects.otherYearHint")}</p>}

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            {catalog?.subjects.map((s) => {
              const chapters = chaptersForYear(catalog, year, s.id);
              const pct = subjectCompletion(catalog, progress, s.id, year);
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => history.push(`/home/subject/${s.id}?year=${year}`)}
                  className="card p-4 text-left active:scale-[0.99] transition relative overflow-hidden">
                  <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-20" style={{ background: s.color }} />
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white text-xl mb-3" style={{ background: s.color }}>
                    <i className={s.icon} />
                  </div>
                  <p className="font-bold text-white text-lg">{s.name}</p>
                  <p className="text-xs text-gray-400 mb-3">
                    {t("subjects.chapterCount", { count: chapters.length })}
                    {s.perDomain ? ` · ${s.perDomain.split("—")[0].trim()}` : ""}
                  </p>
                  <div className="flex items-center gap-2">
                    <ProgressBar value={pct} color={s.color} height={6} className="flex-1" />
                    <span className="text-xs text-gray-300 w-9 text-right">{pct} %</span>
                  </div>
                </button>
              );
            })}
          </div>
          <p className="text-xs text-gray-500 text-center pt-2">{t("subjects.moreSoon")}</p>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Subjects;
